/**
 * The one place that says which personal-profile fields belong to which section,
 * and where each one is stored.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * `edit-profile.tsx` used a single global "Edit" toggle: one button flipped the
 * whole screen into edit mode and one Save submitted every field at once. That
 * made it safe for `useSavePersonalProfile` to write all five columns
 * unconditionally, because every save really did carry every value.
 *
 * Per-section editing removes that guarantee. Saving "General Info" carries no
 * `bio`, so an unconditional `bio: edit.bio?.trim() || null` writes NULL and
 * deletes prose the user never opened. The write succeeds, so nothing surfaces —
 * the same silent-write-failure shape that grep-guard ARCH-06 exists to catch,
 * arriving through the success path instead of the error path.
 *
 * Hence PATCH semantics, which is the entire contract of this module:
 *
 *     key absent       → column untouched
 *     key present, ''  → column set to NULL (a deliberate clear)
 *
 * Those two cases must never collapse into one another.
 *
 * This mirrors `src/lib/investor/profile-edit-fields.ts`, which solved the same
 * class of problem for the investor sections. Same shape, same tests, so the two
 * personas cannot drift apart.
 */

/**
 * Split a typed full name the same way `(startup)/userprofile.tsx` and
 * `buildInvestorNameUpdate` do, so the personas stay consistent.
 *
 * Lives here rather than in `use-save-personal-profile.ts` because the update
 * builder below needs it and the hook imports THIS module — keeping it the
 * other way round would make the two files a cycle.
 */
export function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
  };
}

/**
 * Every field the personal-profile editor renders, keyed by section.
 *
 * `generalInfo` is one section by product decision (2026-08-08): name, company
 * title, location and LinkedIn are the identity block a user thinks of as "who I
 * am", and they are edited together or not at all. `about` is split out because
 * the bio is the one long-form field and already renders as its own card.
 */
export const PERSONAL_EDITOR_FIELDS = {
  generalInfo: ['name', 'functionTitle', 'location', 'linkedIn'],
  about: ['bio'],
} as const;

export type PersonalEditSection = keyof typeof PERSONAL_EDITOR_FIELDS;

/** UI field key → the `PersonalProfileEdit` key it is submitted as. */
const FIELD_TO_EDIT_KEY = {
  name: 'name',
  functionTitle: 'functionTitle',
  location: 'location',
  linkedIn: 'linkedInUrl',
  bio: 'bio',
} as const;

/**
 * Fields this editor must never write.
 *
 * `role` is the dangerous one. `profiles.role` is the authorization trust
 * boundary (`pending` | `startup` | `investor`) and administrative authority
 * lives in `admin_roles` — see the ADMIN AUTHORITY INVARIANT in CLAUDE.md. This
 * screen previously wrote `role: isStartup ? formData.role : 'investor'`, which
 * silently demoted any admin who saved their profile. The job title a founder
 * actually means by "role" is `team_members.function_title`, which is a
 * different table with a different authorization rule, so it is carried here as
 * `functionTitle` and written through the column-scoped
 * `update_my_function_title` RPC.
 *
 * `email` is owned by GoTrue. Changing it is an auth flow with a confirmation
 * step, not a profile patch; an input here accepted text and discarded it.
 */
export const PERSONAL_EDITOR_REMOVED_FIELDS = ['role', 'email'] as const;

const trimmedOrNull = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : t;
};

/**
 * The subset of a user's own profile that a section may change.
 *
 * Every key is optional: a section submits only what it renders, and anything
 * absent is left alone by `buildPersonalProfileDbUpdate`.
 */
export interface PersonalProfilePatch {
  /** Full name as typed; split into first/last on the way to the database. */
  name?: string;
  bio?: string;
  location?: string;
  linkedInUrl?: string;
  /**
   * The caller's role WITHIN their company — Founder, CEO, CTO. Not a persona
   * and not authority. Lives on `team_members`, NOT on `profiles`, so it is
   * deliberately absent from the column update this module builds.
   */
  functionTitle?: string;
}

/**
 * Columns to write to `profiles` for a patch.
 *
 * Only keys actually present on `patch` produce a column. `functionTitle` never
 * appears — it belongs to a different table and travels its own path.
 */
export function buildPersonalProfileDbUpdate(
  patch: PersonalProfilePatch
): Record<string, unknown> {
  const update: Record<string, unknown> = {};

  // `in` rather than a truthiness check: '' means "the user cleared this",
  // which is a write of NULL, not an absence.
  if ('name' in patch) {
    const { firstName, lastName } = splitName(patch.name ?? '');
    update.first_name = firstName || null;
    update.last_name = lastName || null;
  }
  if ('bio' in patch) update.bio = trimmedOrNull(patch.bio);
  if ('location' in patch) update.location = trimmedOrNull(patch.location);
  if ('linkedInUrl' in patch) update.linkedin_url = trimmedOrNull(patch.linkedInUrl);

  return update;
}

/**
 * Turns one section's form data into a patch carrying exactly that section's
 * fields — the bridge between what the editor rendered and what gets written.
 *
 * A field the user left untouched still appears (the editor seeds every input
 * from current values), so an unchanged value is rewritten identically rather
 * than dropped. What matters is that fields from OTHER sections never appear.
 */
export function buildPersonalProfilePatch(
  section: PersonalEditSection,
  data: Record<string, unknown>
): PersonalProfilePatch {
  const patch: PersonalProfilePatch = {};

  for (const field of PERSONAL_EDITOR_FIELDS[section]) {
    if (!(field in data)) continue;
    const key = FIELD_TO_EDIT_KEY[field as keyof typeof FIELD_TO_EDIT_KEY];
    const value = data[field];
    patch[key] = typeof value === 'string' ? value : String(value ?? '');
  }

  return patch;
}
